import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { patchAppointment } from '../services/api';
import './AppointmentActionsModal.css';

// NER-1386 (fase 2 / NER-1478): cancelar o reagendar un turno desde la Agenda.
// PATCH /appointments/{id}: { status:'cancelled' } | { startTime:'YYYY-MM-DDTHH:mm',
// professionalId }. Cancelar/reagendar dispara el WhatsApp al cliente (backend).
const TERMINAL = ['cancelled', 'completed'];

function AppointmentActionsModal({ appointment, professionals = [], onClose }) {
  const { t } = useTranslation();
  const isTerminal = TERMINAL.includes(appointment.status);

  const [date, setDate] = useState(appointment.date || '');
  const [time, setTime] = useState(appointment.time || '');
  const [professionalId, setProfessionalId] = useState(appointment.professionalId || '');
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const clientLabel =
    appointment.clientName || appointment.clientPhone || t('agenda.unknownClient');

  const reschedule = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      setError(t('appointmentActions.dateTimeRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await patchAppointment(appointment.appointmentId, {
        startTime: `${date}T${time}`,
        professionalId: professionalId || appointment.professionalId,
      });
      onClose(true);
    } catch (err) {
      console.error('Error rescheduling appointment:', err);
      setError(t('appointmentActions.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    setSaving(true);
    setError('');
    try {
      await patchAppointment(appointment.appointmentId, { status: 'cancelled' });
      onClose(true);
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      setError(t('appointmentActions.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!saving) onClose(false);
      }}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('appointmentActions.title')}</h2>
        </div>
        <div className="modal-body">
          <dl className="appt-summary">
            <div>
              <dt>{t('appointmentActions.client')}</dt>
              <dd>{clientLabel}</dd>
            </div>
            <div>
              <dt>{t('appointmentActions.service')}</dt>
              <dd>
                {appointment.serviceName || '—'}
                {appointment.durationMin
                  ? ` · ${appointment.durationMin} ${t('agenda.minutesShort')}`
                  : ''}
              </dd>
            </div>
            <div>
              <dt>{t('appointmentActions.status')}</dt>
              <dd>{t(`agenda.status.${appointment.status}`, appointment.status || '—')}</dd>
            </div>
          </dl>

          {error && <div className="error-message">{error}</div>}

          {isTerminal ? (
            <p className="appt-terminal-note">{t('appointmentActions.terminalNote')}</p>
          ) : (
            <>
              <form className="appt-reschedule" onSubmit={reschedule}>
                <h3 className="appt-section-title">{t('appointmentActions.reschedule')}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="appt-date">{t('appointmentActions.date')}</label>
                    <input
                      id="appt-date"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="appt-time">{t('appointmentActions.time')}</label>
                    <input
                      id="appt-time"
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="appt-prof">{t('appointmentActions.professional')}</label>
                  <select
                    id="appt-prof"
                    value={professionalId}
                    onChange={(e) => setProfessionalId(e.target.value)}
                  >
                    {professionals.map((p) => (
                      <option key={p.professionalId} value={p.professionalId}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('common.saving') : t('appointmentActions.rescheduleBtn')}
                </button>
              </form>

              <div className="appt-cancel-zone">
                <h3 className="appt-section-title">{t('appointmentActions.cancelTitle')}</h3>
                {confirmingCancel ? (
                  <>
                    <p className="appt-cancel-warning">{t('appointmentActions.cancelWarning')}</p>
                    <div className="appt-cancel-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setConfirmingCancel(false)}
                        disabled={saving}
                      >
                        {t('appointmentActions.keepAppointment')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={cancel}
                        disabled={saving}
                      >
                        {saving ? t('common.saving') : t('appointmentActions.confirmCancel')}
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => setConfirmingCancel(true)}
                    disabled={saving}
                  >
                    {t('appointmentActions.cancelBtn')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onClose(false)}
            disabled={saving}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AppointmentActionsModal;
